import React, { FC, Ref, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from '@reach/router';
import styled from 'styled-components';
import {
  Question,
  QuestionOption,
  QuizAnswer,
  Quiz as QuizType,
} from 'types/quiz';

import { LoadingPage, PageContainer, Seo } from 'components';
import Header from 'components/Header';
import StarsBackgroundImage from '../../../assets/images/quiz/background/stars-background.png';
import CoupleBackgroundImage from '../../../assets/images/quiz/background/couple.png';
import MountainsBackgroundImage from '../../../assets/images/quiz/background/mountains.png';
import Progress from './components/Progress';
import Single from './components/Single';
import SingleByRelationshipStatus from './components/SingleByRelationshipStatus';
import MultipleAnswer from './components/MultipleAnswer';
import { mobile, smLaptop, tablet } from 'styles/breakpoints';
import { AppState } from 'state/types';
import { useRouter } from 'apis/history';
import {
  resetQuizAnswers,
  updateQuizAnswers,
  updateQuizFlowAnswers,
} from 'state/user/effects';
import Landing from './components/Landing';
import Landing2 from './components/Landing2';
import { useFirstVisitDate, usePageView } from 'utils/hooks';
import BookBuild from './components/BookBuild';
import BookReview from './components/BookReview';
import CircularProgressBar from './components/CircularProgressBar';
import InfoBottomImage from './components/InfoBottomImage';
import InfoTopImg from './components/InfoTopImage';
import Info from './components/Info';
import theme from 'utils/theme';
import BlackFridayBanner from 'components/BlackFridayBanner';
import Tracking from 'utils/tracking';
import PersonalizeBook from './components/PersonalizeBook';
import { DateTime } from 'luxon';
import BirthDateSelector from './components/BirthDateSelector';
import InfoImageUnorderedList from './components/InfoImageUnorderedList';
import ZodiacInfo from './components/ZodiacInfo';
import CircularProgressBarReview from './components/CircularProgressBarReview';
import CircularProgressBarInfo from './components/CircularProgressBarInfo';
import MultipleByRelationshipStatus from './components/MultipleByRelationshipStatus';
import CircularProgressBarList from './components/CircularProgressBarList';
import TimePicker from './components/TimePicker';
import PlaceInput from './components/PlaceInput';
import InfoList from './components/InfoList';
import { normalizeStates } from 'utils/localization';
import { Horoscope, Origin } from 'circular-natal-horoscope-js';

enum QuestionType {
  Single = 'single',
  SingleByRelationshipStatus = 'single_by_relationship_status',
  Multiple = 'multiple',
  MultipleByRelationshipStatus = 'multiple_by_relationship_status',
  BirthDateSelector = 'date_selector',
  InfoImageUnorderedList = 'info_image_unordered_list',
  CircularProgressBar = 'circular_progressbar',
  CircularProgressBarReview = 'circular_progressbar_review',
  CircularProgressBarInfo = 'circular_progressbar_info',
  CircularProgressBarList = 'circular_progressbar_list',
  Info = 'info',
  InfoList = 'info_list',
  InfoTopImg = 'info_top_image',
  InfoBottomImg = 'info_bottom_image',
  PersonalizeBook = 'personalize_book',
  BookBuild = 'book_build',
  BookReview = 'book_review',
  Landing = 'landing',
  ZodiacInfo = 'zodiac_info',
  TimePicker = 'time_picker',
  PlaceInput = 'place_input',
}

const resolveBackgroundImage = backgroundImage => {
  switch (backgroundImage) {
    case 'stars':
      return StarsBackgroundImage;
    case 'couple':
      return CoupleBackgroundImage;
    case 'mountains':
      return MountainsBackgroundImage;
    default:
      return 'none';
  }
};

const getZodiacSign = (dateString: string) => {
  const date = new Date(dateString);

  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  const origin = new Origin({
    year: year,
    month: month - 1,
    date: day,
    hour: 22,
    minute: 0,
    latitude: 35.125801,
    longitude: -117.9859038,
  });

  const horoscope = new Horoscope({
    origin,
  });

  return horoscope.SunSign.key;
};

const ContentContainer = styled.div<{
  questionChanged: boolean;
  hasBackground?: boolean;
  bgColor?: string;
  backgroundImage?: string;
}>`
  opacity: ${({ questionChanged }) => (questionChanged ? 1 : 0)};
  transition: opacity 0.25s;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${({ bgColor }) => bgColor};
  position: relative;
  width: 100%;
  min-height: calc(100vh - 72px);

  ${({ hasBackground, backgroundImage }) =>
    hasBackground &&
    `
    &:before {
      content: "";
      background: 
        url(${backgroundImage}) center bottom / cover no-repeat, #041C3C;
      background-size: cover;
      background-attachment: fixed;
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: -1;
    }
  `}
`;

const HeaderContainer = styled.div`
  @media ${mobile} {
    padding-top: unset;
  }
`;

const BackgroundImage = styled.div<{ bgColor?: string; bgImg?: string }>`
  display: flex;
  flex-direction: column;
  background-size: cover;
  z-index: 0;
  background-color: ${({ bgColor }) => (bgColor ? bgColor : '')};
  @media ${mobile} {
  }
`;

const QuizContainer = styled.div<{ bgColor?: string }>`
  background-color: ${({ bgColor }) => bgColor};
  z-index: 0;
`;

const Quiz: FC = () => {
  const location = useLocation();
  const q = new URLSearchParams(location.search).get('q') ?? '1';
  const [step, setStep] = useState<number>(Number(q) - 1);
  const [prevStep, setPrevStep] = useState<number>(1);
  const [questionChanged, setQuestionChanged] = useState<boolean>(false);
  const [selectedOptions, setSelectedOptions] = useState<QuestionOption[]>([]);
  const [singleChanged, setSingleChange] = useState<boolean>(false);
  const { user } = useSelector((state: AppState) => state.user);
  const [skippedQuestion, setSkippedQuestion] = useState<Number[]>([]);
  const { countdownDiffInMS } = useFirstVisitDate();
  const quiz_answers = useSelector(
    (state: AppState) => state.user?.quiz_answers,
  );
  useEffect(() => {
    setPrevStep(step + 1);
  }, [step]);
  const geo = useSelector((state: AppState) => state.user?.geolocation);
  const userIp = useSelector((state: AppState) => state.user?.user_ip);

  const questions = useSelector((state: AppState) => state.funnel.questions);
  const {
    goToCalculating,
    goToFinalizing,
    goToQuizQuestion,
    navigateBack,
    goToQuiz,
  } = useRouter();

  const dispatch = useDispatch();

  const contentContainerRef: Ref<HTMLDivElement> = useRef(null);

  usePageView({
    city: encodeURIComponent(
      geo?.city?.toLowerCase().replace(/[^a-z0-9]/g, '') || '',
    ),
    country: geo?.iso_country?.toLowerCase(),
    state: normalizeStates(geo?.iso_country || '', geo?.iso_state || ''),
    gender: quiz_answers?.gender ? quiz_answers?.gender[0] : null,
  });

  const question = questions[step];
  const quiz = new URLSearchParams(location.search).get('qz') ?? QuizType.Main;
  const resolvedBackground = resolveBackgroundImage(question?.backgroundImage);
  const pageUrl = window.location.pathname + window.location.search;

  useEffect(() => {
    setTimeout(() => {
      window.scroll({ top: -1, left: 0, behavior: 'smooth' });
    }, 10);
  }, [window.location.href, location.pathname]);

  useEffect(() => {
    setQuestionChanged(true);
    setSingleChange(true);

    let gender = sessionStorage.getItem('genderAnswer');

    if (!gender) {
      return;
    }

    dispatch(updateQuizAnswers({ gender }));
  }, []);

  useEffect(() => {
    const question = new URLSearchParams(location.search).get('q');
    if (!question) {
      return setStep(0);
    }

    if (Number(question) <= questions.length && Number(question) > 0) {
      setStep(Number(question) - 1);
    }
  }, [location]);

  const logEvent = (data: { category: string; label?: string }) => {
    Tracking.trackQuizQuestionAnswered({
      event: `Quiz question answered - ${quiz}`,
      category: `${q} - ${data.category}`,
      label: data.label || '',
    });
  };

  const handleNextQuestion = (answer: QuizAnswer) => {
    if (!questionChanged) {
      return;
    }
    Tracking.virtualPageView(
      {
        city: encodeURIComponent(
          geo?.city?.toLowerCase().replace(/[^a-z0-9]/g, '') || '',
        ),
        country: geo?.iso_country?.toLowerCase(),
        state: normalizeStates(geo?.iso_country || '', geo?.iso_state || ''),
        pageUrl: pageUrl,
        pageTitle: window.location.pathname,
        location: geo?.iso_country?.toLocaleLowerCase(),
      },
      {
        city: encodeURIComponent(
          geo?.city?.toLowerCase().replace(/[^a-z0-9]/g, '') || '',
        ),
        country: geo?.iso_country?.toLowerCase(),
        state: normalizeStates(geo?.iso_country || '', geo?.iso_state || ''),
        gender: quiz_answers?.gender ? quiz_answers?.gender[0] : null,
      },
      userIp || '',
    );

    logEvent({
      category: `${question.label}${
        question.optionsLabel ? ` - ${question.optionsLabel}` : ''
      }`,
      label: String(answer.label),
    });

    delete answer.label;
    setStep(step + 1);

    dispatch(updateQuizAnswers(answer));

    setQuestionChanged(false);

    setSelectedOptions([]);

    if (questions.length - 1 === step) {
      return goToFinalizing();
    }

    goToQuizQuestion();

    setQuestionChanged(true);
    setSingleChange(true);
  };

  const handleSingleQuestion = (option: QuestionOption) => {
    const answer: QuizAnswer = {
      [String(question.key)]: option.value,
      label: option.label || option.value,
    };

    setSelectedOptions([option]);

    if (!singleChanged) {
      return;
    }
    setSingleChange(false);

    setTimeout(() => handleNextQuestion(answer), 50);
  };

  const handleBookSelection = (answer: QuizAnswer) => {
    handleNextQuestion(answer);
  };

  const handleBackClick = () => {
    const isSkipped = skippedQuestion?.includes(step - 1);
    if (isSkipped) {
      navigateBack(-2);
    } else {
      navigateBack();
    }
  };

  const bookReviewHandleBack = () => {
    setStep(step - 3);
    navigateBack(-3);
  };

  const handleSkip = () => {
    setSkippedQuestion(prevValue => [...prevValue, step]);
    setStep(step + 1);

    if (!question || question === null) {
      return goToFinalizing();
    }

    goToQuizQuestion();
    setQuestionChanged(true);
    setSingleChange(true);
  };

  const handleMultipleQuestion = (
    option: QuestionOption,
    maxSelection?: number,
  ) => {
    const updatedSelectedOptions = () => {
      const foundOption = selectedOptions.find(
        selectedOption => selectedOption.value === option.value,
      );
      const isNoneSelected = selectedOptions.find(
        selectedOption => selectedOption.value === 'none',
      );

      const maxSelectionLimit = maxSelection
        ? selectedOptions.length < maxSelection
        : true;

      if (!foundOption && option.value === 'none') {
        return [option];
      }

      if (isNoneSelected) {
        const arrayWithoutNone = selectedOptions.filter(
          selectedOption => selectedOption.value !== 'none',
        );
        return [...arrayWithoutNone, option];
      }
      if (!foundOption && maxSelectionLimit) {
        return [...selectedOptions, option];
      }

      return selectedOptions.filter(
        selectedOption => selectedOption.value !== option.value,
      );
    };

    setSelectedOptions(updatedSelectedOptions());
  };

  const handleInputQuestion = (value: object) => {
    const answer: QuizAnswer = {
      [String(question.key)]: value,
    };
    handleNextQuestion(answer);
  };

  const handleBookConfirm = (value: { [key: string]: string }) => {
    const planets = { planets: value };
    dispatch(updateQuizAnswers(planets));
    goToFinalizing();
  };

  const handleDateInputQuestion = (value: Date) => {
    const convertedDateObject = DateTime.fromJSDate(new Date(value));

    const dateIsoString = convertedDateObject.toISO() || '';
    const zodiacSign = getZodiacSign(dateIsoString);
    const answer: QuizAnswer = {
      [String(question.key)]: dateIsoString,
      zodiacSign: zodiacSign,
    };

    handleNextQuestion(answer);
  };

  const handlePlaceInputQuestion = (value: {
    coordinates: {
      lat: string;
      lng: string;
    };
    place_id: string;
    main_text: string;
    secondary_text: string;
    full_text: string;
    input_text: string;
  }) => {
    const answer: QuizAnswer = {
      [String(question.key)]: value,
    };

    handleNextQuestion(answer);
  };

  const handleTimeInput = (value: {
    hours: number;
    minutes: number;
    ampm: string;
  }) => {
    const answer: QuizAnswer = {
      [String(question.key)]: value,
    };

    handleNextQuestion(answer);
  };

  const handleContinue = () => {
    const values = selectedOptions
      .map(selectedOption => selectedOption.value)
      .join('|');

    const answer: QuizAnswer = {
      [String(question.key)]: values,
      label: values,
    };
    handleNextQuestion(answer);
  };

  const handleLandingOptionSelect = (answer: QuizAnswer) => {
    Tracking.virtualPageView(
      {
        city: encodeURIComponent(
          geo?.city?.toLowerCase().replace(/[^a-z0-9]/g, '') || '',
        ),
        country: geo?.iso_country?.toLowerCase(),
        state: normalizeStates(geo?.iso_country || '', geo?.iso_state || ''),
        pageUrl: pageUrl,
        pageTitle: window.location.pathname,
        location: geo?.iso_country?.toLocaleLowerCase(),
      },
      {
        city: encodeURIComponent(
          geo?.city?.toLowerCase().replace(/[^a-z0-9]/g, '') || '',
        ),
        country: geo?.iso_country?.toLowerCase(),
        state: normalizeStates(geo?.iso_country || '', geo?.iso_state || ''),
        gender: quiz_answers?.gender ? quiz_answers?.gender[0] : null,
      },
      userIp || '',
    );
    logEvent({
      category: `${question.label}${
        question.optionsLabel ? ` - ${question.optionsLabel}` : ''
      }`,
      label: String(answer.label),
    });
    delete answer.label;
    dispatch(updateQuizAnswers(answer));
    setStep(step + 1);
    if (questions.length - 1 === step || question === null) {
      goToFinalizing();
    } else {
      goToQuizQuestion();
    }
  };
  const renderQuestion = useMemo(() => {
    switch (question?.type) {
      case QuestionType.Single:
        return (
          <Single
            question={question}
            selectedOptions={selectedOptions}
            disabled={!questionChanged || !singleChanged}
            onSelect={handleSingleQuestion}
          />
        );
      case QuestionType.SingleByRelationshipStatus:
        return (
          <SingleByRelationshipStatus
            question={question}
            selectedOptions={selectedOptions}
            disabled={!questionChanged || !singleChanged}
            onSelect={handleSingleQuestion}
          />
        );
      case QuestionType.Multiple:
        return (
          <MultipleAnswer
            question={question}
            selectedOptions={selectedOptions}
            disabled={!selectedOptions.length}
            onSelect={handleMultipleQuestion}
            onContinue={handleContinue}
          />
        );
      case QuestionType.MultipleByRelationshipStatus:
        return (
          <MultipleByRelationshipStatus
            question={question}
            selectedOptions={selectedOptions}
            disabled={!selectedOptions.length}
            onSelect={handleMultipleQuestion}
            onContinue={handleContinue}
          />
        );
      case QuestionType.BirthDateSelector:
        return (
          <BirthDateSelector
            {...question}
            onContinue={handleDateInputQuestion}
          />
        );
      case QuestionType.InfoImageUnorderedList:
        return (
          <InfoImageUnorderedList {...question} onContinue={handleContinue} />
        );
      case QuestionType.InfoList:
        return <InfoList {...question} onContinue={handleContinue} />;
      case QuestionType.Info:
        return <Info {...question} onContinue={handleContinue} />;
      case QuestionType.CircularProgressBar:
        return (
          <CircularProgressBar {...question} onComplete={handleContinue} />
        );
      case QuestionType.CircularProgressBarReview:
        return (
          <CircularProgressBarReview
            {...question}
            onComplete={handleContinue}
          />
        );
      case QuestionType.CircularProgressBarList:
        return (
          <CircularProgressBarList {...question} onComplete={handleContinue} />
        );
      case QuestionType.CircularProgressBarInfo:
        return (
          <CircularProgressBarInfo {...question} onComplete={handleContinue} />
        );
      case QuestionType.InfoTopImg:
        return <InfoTopImg {...question} onContinue={handleContinue} />;
      case QuestionType.InfoBottomImg:
        return <InfoBottomImage {...question} onContinue={handleContinue} />;
      case QuestionType.PlaceInput:
        return (
          <PlaceInput {...question} onContinue={handlePlaceInputQuestion} />
        );
      case QuestionType.ZodiacInfo:
        return <ZodiacInfo {...question} onContinue={handleContinue} />;
      case QuestionType.PersonalizeBook:
        return (
          <PersonalizeBook
            questionKey={question.key}
            {...question}
            onContinue={handleInputQuestion}
            onSkip={handleSkip}
          />
        );
      case QuestionType.BookBuild:
        return <BookBuild question={question} onSelect={handleBookSelection} />;
      case QuestionType.TimePicker:
        return (
          <TimePicker
            {...question}
            onContinue={handleTimeInput}
            onSkip={handleTimeInput}
          />
        );

      case QuestionType.BookReview:
        return (
          <BookReview
            question={question}
            onContinue={handleBookConfirm}
            handleBack={bookReviewHandleBack}
          />
        );
      default:
        return null;
    }
  }, [question, questionChanged, selectedOptions, singleChanged]);

  if (!questions?.length) {
    return <LoadingPage />;
  }
  return (
    <>
      <Seo />
      {question?.type === QuestionType.Landing ? (
        <Landing
          question={question}
          onOptionSelect={handleLandingOptionSelect}
        />
      ) : (
        <QuizContainer bgColor={theme.colors.light0} ref={contentContainerRef}>
          {questions[0]?.bfBanner ? (
            <BlackFridayBanner
              {...questions[0].bfBanner}
              countdownDiffInMS={countdownDiffInMS}
            />
          ) : null}
          <HeaderContainer>
            <Header
              logoVariant="center"
              countTotal={questions.length}
              count={step + 1}
              onBack={
                !question?.hideCountAndProgressBar
                  ? () => handleBackClick()
                  : undefined
              }
              sticky={false}
              hideCount={!!question?.hideCountAndProgressBar}
              color={'coldGrey'}
              hasDivider={!!question?.hideCountAndProgressBar}
              hasBackground={question?.hasBackground}
            />
            <Progress
              step={step}
              questionsLength={questions.length}
              hideProgressBar={!!question?.hideCountAndProgressBar}
              onBackClick={handleBackClick}
              hasBackground={question?.hasBackground}
            />
          </HeaderContainer>
          <PageContainer with100vh>
            <BackgroundImage bgColor={'#F3F3F8'}>
              <ContentContainer
                questionChanged={questionChanged}
                key={step}
                hasBackground={question?.hasBackground}
                backgroundImage={resolvedBackground}
              >
                {renderQuestion}
              </ContentContainer>
            </BackgroundImage>
          </PageContainer>
        </QuizContainer>
      )}
    </>
  );
};

export default Quiz;
